import { supabase } from '@/lib/supabase';
import { useFocusEffect } from 'expo-router';
import { useCallback, useState } from 'react';

export interface Note {
  id: string;
  title: string | null;
  created_at: string | null;
}

export interface Quiz {
  id: string;
  title: string | null;
  created_at: string | null;
  lastScore?: number | null; // ✅ Score sur 20
  lastTotal?: number | null; // ✅ Total de questions
}

export interface FlashcardSet {
  id: string;
  title: string | null;
  created_at: string | null;
}

export interface ContentItem {
  id: string;
  title: string | null;
  created_at: string | null;
  type: 'fiche' | 'quiz' | 'flashcards';
  lastScore?: number | null; // ✅ Score sur 20 (uniquement pour quiz)
  lastTotal?: number | null; // ✅ Total de questions
}

export function useRevisionContent() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [flashcardSets, setFlashcardSets] = useState<FlashcardSet[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        setLoading(false);
        return;
      }

      const userId = sessionData.session.user.id;

      // Récupérer notes, quizzes, flashcards
      const [notesRes, quizzesRes, flashcardsRes] = await Promise.all([
        supabase
          .from('notes')
          .select('id, title, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
          
        supabase
          .from('quizzes')
          .select('id, title, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false }),
          
        supabase
          .from('flashcard_sets')
          .select('id, title, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          
      ]);

      const fetchedQuizzes = quizzesRes.data || [];

      // ✅ Récupérer le dernier score pour chaque quiz
      let quizzesWithScores: Quiz[] = fetchedQuizzes;

      if (fetchedQuizzes.length > 0) {
        const quizIds = fetchedQuizzes.map(q => q.id);

        // Récupérer tous les résultats pour ces quiz
        const { data: resultsData } = await supabase
          .from('quiz_results')
          .select('quiz_id, score, total, created_at')
          .eq('user_id', userId)
          .in('quiz_id', quizIds)
          .order('created_at', { ascending: false });

        // Créer une map avec le dernier score par quiz
        const lastScoreByQuiz: Record<string, { score: number; total: number }> = {};

        if (resultsData) {
          for (const result of resultsData) {
            // On ne garde que le premier (le plus récent car trié desc)
            if (!lastScoreByQuiz[result.quiz_id]) {
              lastScoreByQuiz[result.quiz_id] = {
                score: result.score,
                total: result.total,
              };
            }
          }
        }

        // Ajouter les scores aux quiz
        quizzesWithScores = fetchedQuizzes.map(quiz => {
          const scoreData = lastScoreByQuiz[quiz.id];
          if (scoreData) {
            // Convertir en note sur 20
            const scoreOn20 = Math.round((scoreData.score / scoreData.total) * 20);
            return {
              ...quiz,
              lastScore: scoreOn20,
              lastTotal: 20,
            };
          }
          return {
            ...quiz,
            lastScore: null,
            lastTotal: null,
          };
        });
      }

      setNotes(notesRes.data || []);
      setQuizzes(quizzesWithScores);
      setFlashcardSets(flashcardsRes.data || []);
    } catch (error) {
      console.error('Error fetching revision content:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // useFocusEffect : refetch à chaque fois que l'écran devient actif
  useFocusEffect(
    useCallback(() => {
      fetchContent();
    }, [fetchContent])
  );

  // Tous les items combinés et triés
  const getAllItems = (): ContentItem[] => {
    const allItems: ContentItem[] = [
      ...notes.map(n => ({ ...n, type: 'fiche' as const })),
      ...quizzes.map(q => ({ 
        ...q, 
        type: 'quiz' as const,
        lastScore: q.lastScore,
        lastTotal: q.lastTotal,
      })),
      ...flashcardSets.map(f => ({ ...f, type: 'flashcards' as const })),
    ];

    return allItems.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  };

  const getFilteredItems = (filter: 'all' | 'fiches' | 'quiz' | 'flashcards'): ContentItem[] => {
    let items: ContentItem[] = [];

    if (filter === 'all' || filter === 'fiches') {
      items = [...items, ...notes.map(n => ({ ...n, type: 'fiche' as const }))];
    }
    if (filter === 'all' || filter === 'quiz') {
      items = [...items, ...quizzes.map(q => ({ 
        ...q, 
        type: 'quiz' as const,
        lastScore: q.lastScore,
        lastTotal: q.lastTotal,
      }))];
    }
    if (filter === 'all' || filter === 'flashcards') {
      items = [...items, ...flashcardSets.map(f => ({ ...f, type: 'flashcards' as const }))];
    }

    return items.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });
  };

  const items = getAllItems();

  return {
    notes,
    quizzes,
    flashcardSets,
    items,
    loading,
    getFilteredItems,
    refresh: fetchContent,
  };
}
